/** What each preview row shows and how its numbers read (screens §P6 Preview; §1.4 formats). Presentation arithmetic only. */
import { limitReading } from "./pastLimit";
import { TWIN_DEFINITION, changeScaleOf } from "@gxs/metric-defs";
import { KPI_DEFS, PREVIEW_GATED_KPIS, type KpiId, type PreviewResult, type PreviewValue } from "@bsim/domain";
import { LOWER_IS_BETTER, kpiLabel, labelOf } from "../format/labels";
import { QUARTER_END_LOW_PLAN_LABEL, YEAR_END_PLAN_LABEL, type RiskProjection } from "../api/riskFields";
import { formatBp, formatKpi, formatPrintedChange, formatPoints, formatPp, formatRate, formatUsdPerShare, scoringYearLabel } from "../format";

export interface PreviewRow {
  readonly id: string;
  /** The KPI whose point, range, delta, tier and drivers the row shows. */
  readonly kpi: KpiId;
  /** The row's since-year-end causes (the engine's bridge for the measure) open from its change figure. */
  readonly bridged?: true;
  readonly label: string;
  /** Names its date: a share price reads as two dates beside the cockpit's close, so the row says "projected end of FY2026". */
  readonly dated?: boolean;
  /** The moment the figure is read at, said on the row ("year-end on your plan"): a reading that has two moments names the one shown. */
  readonly moment?: string;
}

/** In display order (screens §P6); no Value Created row (look-through, P9 only). */
export const PREVIEW_ROWS: readonly PreviewRow[] = [
  { id: "price", kpi: "price", label: "Share price", dated: true },
  { id: "nim", kpi: "nim", label: labelOf("nim"), moment: YEAR_END_PLAN_LABEL },
  { id: "ncoRate", kpi: "ncoRate", label: labelOf("ncoRate") },
  { id: "cet1", kpi: "cet1Reported", label: labelOf("cet1Reported"), bridged: true },
  { id: "cet1Marked", kpi: "cet1Marked", label: labelOf("cet1Marked"), bridged: true },
  { id: "lcrLite", kpi: "lcrLite", label: labelOf("lcrLite") },
  { id: "eve200", kpi: "eve200", label: labelOf("eve200"), moment: YEAR_END_PLAN_LABEL },
  { id: "rotce", kpi: "rotce", label: labelOf("rotce") },
  { id: "efficiencyRatio", kpi: "efficiencyRatio", label: labelOf("efficiencyRatio") },
];

export const isGated = (row: PreviewRow): boolean => (PREVIEW_GATED_KPIS as readonly KpiId[]).includes(row.kpi);

/**
 * A KPI as the row reads it: the year-end month (ADR-0053 `yearEnd`), the basis the cockpit, the expectation strip and the results quote, never the window's average. The
 * likely range and its tier come from the window's reply only while they still bracket the year-end point; otherwise the row is a single point. A result with no `yearEnd`
 * (a reply from before it existed) reads as the window.
 */
export function kpiOf(result: PreviewResult, kpi: KpiId): PreviewValue | undefined {
  const window = result.kpis[kpi], end = result.yearEnd?.[kpi];
  if (end === undefined || end.status === "gated" || window === undefined || window.status === "gated") return end ?? window;
  const inside = window.low <= end.point && end.point <= window.high;
  return { ...window, ...(end.quarterEndLow === undefined ? {} : { quarterEndLow: end.quarterEndLow }), point: end.point, low: inside ? window.low : end.point, high: inside ? window.high : end.point, confidence: inside ? window.confidence : null, drivers: inside ? window.drivers : [] };
}

/** The KPI's move against the no-change plan on the same year-end basis. */
export const deltaOfKpi = (result: PreviewResult, kpi: KpiId): PreviewValue | undefined =>
  result.yearEnd === undefined ? result.deltasVsAutopilot[kpi] : result.yearEndDeltasVsNoChange?.[kpi];

/**
 * The panel's EVE reading is the draft's `riskProjection` (the memo's year-end figure), whatever the browser's own run says: the point is the projection's (the same run as the browser's, to 1e-9) and the move against the no-change plan
 * is the browser's own, from that one run, so the row, the memo, the review and the cards print one number. A result without a shown year-end EVE is returned as it came.
 */
export function withRiskProjection(result: PreviewResult, projection: RiskProjection | undefined): PreviewResult {
  const end = result.yearEnd?.eve200;
  if (projection === undefined || result.yearEnd === undefined || end === undefined || end.status !== "shown") return result;
  // One run per reading: the Worker's own quarter-end low is dropped when the projection has none.
  const { quarterEndLow: _worker, ...endRest } = end;
  return {
    ...result,
    yearEnd: { ...result.yearEnd, eve200: { ...endRest, point: projection.eve200, low: Math.min(end.low, projection.eve200), high: Math.max(end.high, projection.eve200), ...(projection.eve200QuarterEndLow === undefined ? {} : { quarterEndLow: projection.eve200QuarterEndLow }) } },
  };
}

/** Two decision trees with the same values (key order is not a difference). */
export function sameDecisions(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  const [ka, kb] = [Object.keys(a), Object.keys(b)];
  return ka.length === kb.length && ka.every((k) => k in b && sameDecisions((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/**
 * The server's saved risk projection describes the draft as last SAVED. It is used for the panel only while it belongs to the draft on screen (no decision changed and not yet saved); with an unsaved or in-flight change the browser's own
 * run is the fresh reading, and a stale projection must not override it (it would cancel the move to 0.0 and keep the old state).
 */
export const projectionOnScreen = (projection: RiskProjection | undefined, onScreen: unknown, saved: unknown): RiskProjection | undefined => (sameDecisions(onScreen, saved) ? projection : undefined);

export const shown = (v: PreviewValue | undefined): Extract<PreviewValue, { status: "shown" }> | null => (v?.status === "shown" ? v : null);


export const LOOK_THROUGH_LABEL = "Official score if the market follows the consensus forecast";
/** Under the score row: the twin it is measured against runs on the same forecast. */
export const SCORE_BASIS_LINE = `Your status-quo twin (${TWIN_DEFINITION.charAt(0).toLowerCase()}${TWIN_DEFINITION.slice(1).replace(/\.$/, "")}) is run on the same consensus forecast.`;

/**
 * The score's range on the forecast's low, central and high rate paths ("range −1.8pp to +4.2pp on the forecast's low, central and high rate paths"), from `low` and `high` where the estimate carries them
 * (each an estimate with `valueCreatedRate`); null when it does not, so the row reads the point alone and never a placeholder.
 */
export function scoreRangeText(lt: NonNullable<PreviewResult["lookThrough"]>): string | null {
  const [low, high] = [lt.low?.valueCreatedRate, lt.high?.valueCreatedRate];
  // The point is inside its own range: the ends are picked in dollars, so the rate ends are widened to hold the point's rate.
  return low === undefined || high === undefined ? null : `Range ${formatPp(Math.min(low, high, lt.valueCreatedRate), { signed: true }).replace(/ points$/, "")} to ${formatPp(Math.max(low, high, lt.valueCreatedRate), { signed: true }).replace(/ points$/, " percentage points")} on the forecast's low, central and high rate paths`;
}

/** From round 2 the row's number is what this plan changes against the no-change plan, so it says so (the level sits beside it). */
export const CHANGE_FROM_PLAN_LABEL = "Official score · change from this plan, if the market follows the consensus forecast";

/** "You stand −20.1pp vs your status-quo twin": the official score from the last results. */
export const standingText = (rate: number): string => `You stand ${formatPp(rate, { signed: true })} vs your status-quo twin`;

/** "+0.84 points · +$0.55 a share": the score the resolve would give the draft on the consensus path (ADR-0043). */
export const lookThroughText = (lt: NonNullable<PreviewResult["lookThrough"]>): string =>
  `${formatPp(lt.valueCreatedRate, { signed: true })} · ${formatUsdPerShare(lt.valueCreatedUsdPerShare, { signed: true })} a share`;

/** The draft changes nothing the score counts yet: its score, both halves and its worth at the end all match the no-change plan's (shown as one line, never five zero lines). */
export function noChangeYet(result: PreviewResult): boolean {
  const lt = result.lookThrough;
  if (lt === undefined) return false;
  const cent = (x: number): number => Math.round(x * 100);
  const pt = (x: number): number => Math.round(x * 1e4);
  const base = result.lookThroughNoChange;
  return pt(lt.valueCreatedRate) === 0 && pt(lt.reportedOnlyValueCreatedRate) === 0 && cent(lt.valueCreatedUsdPerShare) === 0 && (base === undefined || cent(lt.terminalPriceUsdPerShare) === cent(base.terminalPriceUsdPerShare));
}
export const NO_CHANGE_YET = "No change yet";

/** "of which +0.02 this year and +2.05 from the two years your plan runs on": the split and its net, so a headline of 0.00pp never sits above a split that is not 0 / 0. */
export function giveBackText(lt: NonNullable<PreviewResult["lookThrough"]>): string {
  // The printed parts must add to the printed total: the total and the first part round to the cent of a pp, the second is what is left.
  const [total, year] = [Math.round(lt.valueCreatedRate * 1e4) / 1e4, Math.round(lt.reportedOnlyValueCreatedRate * 1e4) / 1e4];
  const rest = Math.round((total - year) * 1e4) / 1e4;
  const num = (x: number): string => formatPp(x, { signed: true }).replace(/ points$/, "");
  // A total that rounds to nothing while its parts do not is two moves that cancel: the row says so rather than read as no effect.
  const cancel = total === 0 && (year !== 0 || rest !== 0) ? "These two cancel: " : "";
  return `${cancel}${cancel === "" ? "of which " : ""}${num(year)} this year and ${num(rest)} from the two look-through years`;
}

/**
 * "Worth at the end of year 3, counting what your plan earns over the next two years: $71.68 a share · if you change nothing $60.10": the terminal price the score counts, beside the no-change plan's (the twin's rule).
 * The share price row above is this year's end only; the two figures are named for the date and the years each one covers.
 */
export function valueAtEndText(result: PreviewResult, planWorthSaid = false): string | null {
  const [lt, base] = [result.lookThrough, result.lookThroughNoChange];
  if (lt === undefined) return null;
  const head = `Worth at the ${scoringYearLabel(result.round)}, counting what your plan earns over the next two years: ${formatUsdPerShare(lt.terminalPriceUsdPerShare)} a share`;
  if (base === undefined) return planWorthSaid ? null : head;
  // The score restates the end-of-plan gap in today's dollars (discounted at the cost of equity), so the two differ by that discount: both figures say their basis.
  const gap = Math.round((lt.terminalPriceUsdPerShare - base.terminalPriceUsdPerShare) * 100) / 100, score = Math.round(lt.valueCreatedUsdPerShare * 100) / 100;
  const restated = gap === 0 || gap === score ? "" : `; the score counts that gap in today\u2019s dollars: ${formatUsdPerShare(lt.valueCreatedUsdPerShare, { signed: true })}`;
  // The plan's own worth is stated once: when the worth sentence has said it, this line carries only the no-change figure and the gap.
  if (planWorthSaid) return `If you change nothing the plan is worth ${formatUsdPerShare(base.terminalPriceUsdPerShare)} a share (${formatUsdPerShare(gap, { signed: true })} with yours${restated})`;
  return `${head} \u00b7 if you change nothing ${formatUsdPerShare(base.terminalPriceUsdPerShare)} (${formatUsdPerShare(gap, { signed: true })} by then${restated})`;
}

/** "EVE at +200bp: 0.2pp of headroom; the higher-rate path puts you beyond it (−21.0% against your −20.0% limit)": one self-set limit the plan keeps on the central path and breaks on the forecast's adverse one; the headroom is `limit − onCentralPath`, both from the projection's own warning. */
export function limitWarningText(w: NonNullable<RiskProjection["limitWarnings"]>[number]): string {
  return `${kpiLabel(w.kpi)}: ${formatPrintedChange(w.kpi, Math.abs(w.onCentralPath - w.limit), { signed: false, words: true })} of headroom; the ${w.adversePath}-rate path puts you beyond it (${formatKpi(w.kpi, w.onAdversePath)} against your ${formatKpi(w.kpi, w.limit)} limit)`;
}

/**
 * "This year's price moves +$0.01; the two projected years add +$1.26, and the score counts both": the headline price is this year's end only, so a small price move beside a large score
 * says where the rest comes from. Said whenever the score and the price differ by more than a cent.
 */
export function priceVsScoreLine(priceDelta: number | null, lt: NonNullable<PreviewResult["lookThrough"]>): string | null {
  if (priceDelta === null) return null;
  const year = Math.round(priceDelta * 100) / 100, score = Math.round(lt.valueCreatedUsdPerShare * 100) / 100;
  const rest = Math.round((score - year) * 100) / 100;
  if (score === 0 || rest === 0) return null;
  return `This year\u2019s price moves ${formatUsdPerShare(year, { signed: true })}; the two projected years ${rest > 0 ? "add" : "take"} ${formatUsdPerShare(Math.abs(rest))}, and the score counts both`;
}

/** "Your plan is worth $65.58 a share; the market already pays $66.65 for it. The gap is the market's memory of better years.": said once, only where the engine finds the plan's worth below the price. */
export function worthVsPriceLine(result: PreviewResult): string | null {
  const price = shown(kpiOf(result, "price"));
  if (result.lookThrough === undefined || price === null || valueAtEndReason(result) === null) return null;
  return `Your plan is worth ${formatUsdPerShare(result.lookThrough.terminalPriceUsdPerShare)} a share; the market already pays ${formatUsdPerShare(price.point)} for it.`;
}

/** The row's figure as printed ("11.05%"). */
export function valueText(row: PreviewRow, result: PreviewResult): string | null {
  const main = shown(kpiOf(result, row.kpi));
  return main === null ? null : formatKpi(row.kpi, main.point);
}

/**
 * The no-change baseline (the player's standing policy) for a KPI: this run's point minus its delta against the plan. Every reading of the baseline (the ghost's
 * "if you change nothing" and the baseline-vs-last-year line) goes through here, so both read the same preview result, never an earlier draft version's.
 */
export function noChangePoint(result: PreviewResult, kpi: KpiId): number | null {
  const point = shown(kpiOf(result, kpi));
  const delta = shown(deltaOfKpi(result, kpi));
  return point === null || delta === null ? null : point.point - delta.point;
}

/** The row as the no-change baseline reads it. */
export function noChangeText(row: PreviewRow, result: PreviewResult): string | null {
  const main = noChangePoint(result, row.kpi);
  return main === null ? null : formatKpi(row.kpi, main);
}

/** The plan's own move from last year ("+13bp"), when the baseline itself moved: the draft's delta against the plan alone would read as no move. */
export function planMoveText(kpi: KpiId, result: PreviewResult, lastYear: number | null | undefined): string | null {
  const plan = noChangePoint(result, kpi);
  if (plan === null || lastYear === null || lastYear === undefined || shownDelta(kpi, plan - lastYear) === 0) return null;
  return deltaText(kpi, plan - lastYear);
}

/** "3.53–3.62%", "$60.10–$66.20"; null when the range is the point alone. */
export function rangeText(kpi: KpiId, v: Extract<PreviewValue, { status: "shown" }>): string | null {
  if (v.low === v.high) return null;
  const [low, high] = [formatKpi(kpi, v.low), formatKpi(kpi, v.high)];
  // Ends that print the same ("125–125%") are no range.
  if (low === high) return null;
  // Whole-number ends read "116–126%" (the mockup); the unit is written once on a rate range.
  const whole = (t: string) => t.replace(/\.0(?=%|$)/, "");
  const ends = low.endsWith(".0%") && high.endsWith(".0%") ? [whole(low), whole(high)] : [low, high];
  // A negative end would read "−18.4–−15.9%", so a range that crosses or sits below zero is written "−18.4 to −15.9%".
  const joint = low.startsWith("−") || high.startsWith("−") ? " to " : "–";
  return `${KPI_DEFS[kpi].unit === "rate" ? (ends[0] ?? low).replace(/%$/, "") : ends[0]}${joint}${ends[1]}`;
}

/** NIM, losses, deposit cost and CET1 move in bp; other ratios in pp; price in dollars (`changeScaleOf`, one rule for every screen). */
const isBp = (kpi: KpiId): boolean => KPI_DEFS[kpi].unit === "rate" && changeScaleOf(kpi) === "bp";

/** The step a KPI's move is written in (cents, whole bp or tenths of a pp), as a unit of the raw delta: parts rounded to it add to the printed move. */
export const deltaStep = (kpi: KpiId): number => (KPI_DEFS[kpi].unit === "usdPerShare" ? 0.01 : isBp(kpi) ? 0.0001 : 0.001);

/** The delta as it is written: cents, whole bp or tenths of a pp, so its tone and its text agree. */
function shownDelta(kpi: KpiId, delta: number): number {
  if (KPI_DEFS[kpi].unit === "usdPerShare") return Math.round(delta * 100) / 100;
  if (isBp(kpi)) return Math.round(delta * 10_000);
  return Math.round(delta * 1000) / 10;
}

export function deltaText(kpi: KpiId, delta: number): string {
  const d = shownDelta(kpi, delta);
  if (KPI_DEFS[kpi].unit === "usdPerShare") return formatUsdPerShare(d, { signed: true });
  if (isBp(kpi)) return formatBp(d, { signed: true });
  return formatPoints(d);
}

/** Green when the move helps (a lower NCO rate helps), red when it hurts, grey when it reads as no move. */
export function deltaTone(kpi: KpiId, delta: number): "up" | "dn" | "flat" {
  const d = shownDelta(kpi, delta);
  return d === 0 ? "flat" : (d > 0) !== LOWER_IS_BETTER.has(kpi) ? "up" : "dn";
}

/** The player's own limit and the regulatory minimum for a KPI, when there is one; the preview flags a projected value outside either. */
export interface PreviewLimits {
  readonly own: (kpi: KpiId) => { readonly limit: number; readonly cmp: "le" | "ge" } | null;
  readonly regulatoryMinimum: (kpi: KpiId) => number | null;
  /** The usual limit the game starts every bank with (the one that applies until the player saves limits of their own). */
  readonly usual?: (kpi: KpiId) => { readonly limit: number; readonly cmp: "le" | "ge" } | null;
  /** A limit the game fixes for every bank (the dashboard lists it beside the player's own). */
  readonly fixed?: (kpi: KpiId) => { readonly limit: number; readonly cmp: "le" | "ge" } | null;
  /** What that floor is called ("Regulatory minimum", or the game's own floor on an internal measure); absent = the regulator's. */
  readonly floorLabel?: (kpi: KpiId) => string;
}

/** A projected value outside a limit: the regulator's minimum is the graver one. */
export interface Breach { readonly text: string; readonly level: "regulatory" | "own" }

/** "Below the regulatory minimum 100%", then "Below your floor 110%": each limit `value` lands outside of, the regulator's first. `named` puts the measure's name first (a row that shows two). */
export function kpiBreaches(kpi: KpiId, value: number, limits: PreviewLimits, named: boolean): Breach[] {
  const own = limits.own(kpi);
  const min = limits.regulatoryMinimum(kpi);
  const found: Breach[] = [];
  const write = (level: Breach["level"], phrase: string) => found.push({ level, text: named ? `${kpiLabel(kpi)} ${phrase}` : `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}` });
  if (min !== null && value < min) write("regulatory", `below the ${(limits.floorLabel?.(kpi) ?? "Regulatory minimum").replace(/^[A-Z](?=[a-z])/, (c) => c.toLowerCase())} ${formatKpi(kpi, min)}`);
  if (own !== null && (own.cmp === "ge" ? value < own.limit : value > own.limit)) {
    const read = limitReading(kpi, value, own.limit, true);
    write("own", `${own.cmp === "ge" ? "below your floor" : "above your limit"} ${read.limit}${read.note === null ? "" : ` (reads ${read.value}: ${read.note})`}`);
  }
  return found;
}

/** The own-limit reading of the EVE row: "worst quarter-end on your plan −21.4%", present only where the draft's projection carries it. */
export const QUARTER_END_LOW_LABEL = QUARTER_END_LOW_PLAN_LABEL;

/** The two CET1 measures name themselves in a breach line: the reader sees Reported and Marked side by side and must know which is past. */
const isCapitalKpi = (kpi: KpiId): boolean => kpi === "cet1Reported" || kpi === "cet1Marked";

export function breachesOf(row: PreviewRow, result: PreviewResult, limits: PreviewLimits): Breach[] {
  const named = isCapitalKpi(row.kpi);
  const found = [row.kpi].flatMap((kpi) => {
    const v = shown(kpiOf(result, kpi));
    // The resolve tests the player's own EVE limit on the worst quarter-end of the year, so the preview does too, where the projection carries it.
    return v === null ? [] : kpiBreaches(kpi, kpi === "eve200" ? v.quarterEndLow ?? v.point : v.point, limits, named);
  });
  return found.sort((a, b) => Number(b.level === "regulatory") - Number(a.level === "regulatory"));
}

/**
 * "Inside your limit on the consensus path, but the likely range reaches −21.0% (limit −20.0%)": a point that is fine while the edge of its likely range crosses the player's own
 * limit, so a draft is never called clear only because the expected path is.
 */
export function rangeBreaches(row: PreviewRow, result: PreviewResult, limits: PreviewLimits): Breach[] {
  return [row.kpi].flatMap((kpi): Breach[] => {
    const v = shown(kpiOf(result, kpi));
    const own = limits.own(kpi);
    if (v === null || own === null) return [];
    const level = kpi === "eve200" ? v.quarterEndLow ?? v.point : v.point;
    const inside = own.cmp === "ge" ? level >= own.limit : level <= own.limit;
    const edge = own.cmp === "ge" ? v.low : v.high;
    const crosses = own.cmp === "ge" ? edge < own.limit : edge > own.limit;
    return inside && crosses ? [{ level: "own", text: `${isCapitalKpi(kpi) ? `${kpiLabel(kpi)} inside` : "Inside"} your limit on the consensus path, but the likely range reaches ${formatKpi(kpi, edge)} (limit ${formatKpi(kpi, own.limit)})` }] : [];
  });
}

/** The measures where holding more has a price (liquidity costs margin, idle capital drags returns): far above the player's own floor they earn a neutral line, never a warning. */
const COMFORT: Partial<Record<KpiId, { readonly rate: number; readonly hint: string }>> = {
  lcrLite: { rate: 0.3, hint: "Well above your floor \u2014 extra liquidity costs margin" },
  cet1Reported: { rate: 0.03, hint: "Well above your floor \u2014 idle capital drags returns" },
};
export const floorHint = (kpi: KpiId, value: number, limit: { readonly limit: number; readonly cmp: "le" | "ge" } | null): string | null => {
  const comfort = COMFORT[kpi];
  return comfort !== undefined && limit !== null && limit.cmp === "ge" && value > limit.limit + comfort.rate ? comfort.hint : null;
};

export const DRIVER_WORDS = { rates: "rates-sensitive", rivals: "rival-sensitive", credit: "credit-sensitive" } as const;

/** The row the last edit moved most, as a share of its earlier value; null when nothing moved or a gated value is involved. */
export function mostMoved(before: PreviewResult, after: PreviewResult, gated: boolean): { readonly row: PreviewRow; readonly was: string } | null {
  let best: { row: PreviewRow; was: string; move: number } | null = null;
  for (const row of PREVIEW_ROWS) {
    if (gated && isGated(row)) continue;
    const a = shown(kpiOf(before, row.kpi));
    const b = shown(kpiOf(after, row.kpi));
    if (a === null || b === null || a.point === b.point) continue;
    const move = Math.abs(b.point - a.point) / (Math.abs(a.point) || 1);
    if (best === null || move > best.move) best = { row, was: valueText(row, before) ?? formatKpi(row.kpi, a.point), move };
  }
  return best === null ? null : { row: best.row, was: best.was };
}

/** "The year will differ": what the preview does not include, at reading size beside the headline range. */
export const YEAR_WILL_DIFFER = "The year will differ: the market\u2019s path and surprise cards are not in this number.";

/** "Share price $60.10\u2013$66.20 likely, point $63.40": the headline's range on the price, when the ranges reply has one. */
export function priceHeadline(result: PreviewResult | null): string | null {
  const price = result === null ? null : shown(kpiOf(result, "price"));
  const range = price === null ? null : rangeText("price", price);
  return price === null || range === null ? null : `Share price ${range} likely \u00b7 point ${formatKpi("price", price.point)} on the consensus forecast`;
}

/** The engine's basis, one entry per clause (it joins them with a semicolon); a result without one reads the default. */
export const basisEntries = (result: PreviewResult | null): readonly string[] => (result?.basis === undefined ? ["with your card answers", "before the year\u2019s surprises"] : result.basis.split("; ").map((e) => e.trim().replace(/\.$/, "")).filter((e) => e !== ""));
const WORTH_ENTRY = /^The plan['\u2019]s worth if it runs on/;

/** Why the plan's worth two years on sits below the market's price: the basis entry the engine writes for it ("The plan's worth if it runs on ($X a share) is below …"), whole; the engine says it only where it is below. */
export function valueAtEndReason(result: PreviewResult): string | null {
  return basisEntries(result).find((e) => WORTH_ENTRY.test(e)) ?? null;
}

/**
 * The preview's one honest caveat (ADR-0043): the whole market runs, so the only stated limits are the cards (none fire) and rival teams, which are
 * held at their standing plans; the rows without a range are said to be a single point.
 */
export function previewCaveat(result: PreviewResult | null): string {
  // Each basis entry is its own sentence.
  const base = `${basisEntries(result).filter((e) => !WORTH_ENTRY.test(e)).map((e) => `${e.charAt(0).toUpperCase()}${e.slice(1)}.`).join(" ")} Every rival is on the plan it will play.`;
  if (result === null) return base;
  const held = result.rivalsMode === "humanRivalsHeld" ? " Rival teams are held at their standing plan." : "";
  return `${base}${held}`;
}

/** "If you change nothing: $70.93 (+8% on last year-end)": the no-change plan's price, said once above the rows; every row then compares with it alone. Null when the price or last year's is missing. */
export function noChangeLine(result: PreviewResult, lastYearPrice: number | null | undefined): string | null {
  const row = PREVIEW_ROWS.find((r) => r.dated === true);
  const base = row === undefined ? null : noChangePoint(result, row.kpi);
  if (base === null || lastYearPrice === null || lastYearPrice === undefined || lastYearPrice === 0) return null;
  return `If you change nothing: ${formatUsdPerShare(base)} (${formatRate(base / lastYearPrice - 1, 0, { signed: true })} on last year-end)`;
}
